import axios from 'axios'
import { useQuery } from 'react-query'
import { useAuth } from '../core/Auth'

const API_URL = process.env.REACT_APP_API_URL

export interface PerCompanyPermission {
  company_id: number
  role_id: number
  is_admin: boolean
  is_manager: boolean
  is_coordinator: boolean
  can_manager_tests_feature: boolean
  can_manage_notifications: boolean
  can_manage_whatsapp: boolean
  // Per-company feature flag for the notifications module. The
  // sidebar entry only shows when any of the user's companies has
  // this true; inside the page, company tabs only render for
  // companies that have it on.
  company_notifications_enabled: boolean
  // Premium tier — unlocks reminder strategies (fixed / escalation /
  // daily) in the rule editor. Always false on the free tier.
  company_notifications_premium: boolean
}

export interface PermissionsSummary {
  is_admin_anywhere: boolean
  can_manage_notifications: boolean
  can_manage_whatsapp: boolean
  any_company_has_notifications: boolean
  per_company: PerCompanyPermission[]
  // Manufacturer surface — populated via manufacturer_users links on
  // the backend. Drives the Fabricante / Estoque sidebar entry.
  manufacturer_ids: number[]
  is_manufacturer: boolean
}

async function fetchPermissions(): Promise<PermissionsSummary> {
  const { data } = await axios.get<{ data: PermissionsSummary }>(`${API_URL}/me/permissions`)
  return data.data
}

/**
 * Cached snapshot of the logged-in user's permission flags. Drives sidebar
 * gating and conditional tabs inside the notifications page without each
 * surface re-fetching.
 *
 * Returns a stable shape even before the API responds (all flags false) so
 * callers can `if (perms.can_manage_notifications)` without nullish dance.
 */
export function usePermissions() {
  const { currentUser } = useAuth()
  const enabled = !!currentUser?.id
  const { data, isLoading, refetch } = useQuery(
    ['me', 'permissions', currentUser?.id],
    fetchPermissions,
    {
      enabled,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  )

  const safe: PermissionsSummary = data ?? {
    is_admin_anywhere: false,
    can_manage_notifications: false,
    can_manage_whatsapp: false,
    any_company_has_notifications: false,
    per_company: [],
    manufacturer_ids: [],
    is_manufacturer: false,
  }

  function permFor(company_id: number | null | undefined): PerCompanyPermission | null {
    if (!company_id) return null
    return safe.per_company.find((p) => p.company_id === company_id) ?? null
  }

  return { ...safe, isLoading, refetch, permFor }
}
